import DOMPurify from 'dompurify'

/**
 * Converts a string to kebab-case
 * @param {string} str - The string to convert
 * @returns {string} The kebab-cased string
 */
const toKebabCase = (str) => {
    return str
        .trim()
        .replace(/[\s_]+/g, '-') // collapse spaces/underscores → hyphens
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2') // insert hyphen between camelCase
        .replace(/-+/g, '-') // collapse multiple hyphens
        .replace(/^-+|-+$/g, '') // trim leading/trailing hyphens
        .toLowerCase()
}

/**
 * Converts a string to Title Case. Acronyms are left uppercase, including plurals.
 * @param {string} str - The string to convert
 * @returns {string} The title-cased string
 */
const toTitleCase = (str) => {
    return str.replace(/\b\w+\b/g, (word) => {
        if (/^[A-Z]{2,}s?$/.test(word)) {
            return word
        }

        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
}

/**
 * Converts a string to sentence case (first letter uppercase, rest lowercase)
 * @param {string} str - The string to convert
 * @returns {string} The sentence-cased string
 */
const toSentenceCase = (str) => {
    return str.toLowerCase().replace(/^\w/, (char) => char.toUpperCase())
}

/**
 * Converts a hyphenated/underscored string to a sentence-like string
 * @param {string} str - The string to convert
 * @returns {string} The sentence-like string
 */
const toSentenceString = (str) => {
    // If it already looks like a sentence (has spaces or full stops), leave it alone
    if (str.includes(' ') || str.includes('.')) {
        return str
    }
    return str
        .replace(/[-_]+/g, ' ')
        .toLowerCase()
        .replace(/^\w/, (char) => char.toUpperCase())
}

/**
 * Generates a unique key based on a string and a random suffix
 * @param {string} str - The base string
 * @returns {string} The generated key
 */
const makeKey = (str) => {
    return `${toKebabCase(str)}-${Math.random().toString().substring(2, 8)}`
}

/**
 * Generates a UUID v4 (random) string
 * @returns {string} The generated UUID
 */
export const createUUIDv4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0,
            v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })
}

/**
 * Checks if a string contains common markdown syntax
 * @param {string} str - The string to check
 * @returns {boolean} True if the string likely contains markdown
 */
const isMarkdown = (str) => {
    const markdownSyntax = /[*#\[\]_`]|(?:\r?\n){2,}/
    return markdownSyntax.test(str)
}

/**
 * Sanitizes HTML using DOMPurify with a predefined list of allowed tags and attributes
 * @param {string} dirty - The dirty HTML string
 * @param options
 * @returns {string} The sanitized HTML string
 */
const sanitizeHTML = (
    dirty,
    options = { tags: [], attrs: [], purifyOptions: {} }
) => {
    const customAttrs = Array.isArray(options.attrs) ? options.attrs : []
    const customTags = Array.isArray(options.tags) ? options.tags : []

    return DOMPurify.sanitize(dirty, {
        ALLOWED_TAGS: [
            'a',
            'b',
            'code',
            'em',
            'i',
            'ul',
            'ol',
            'li',
            'p',
            'pre',
            'strong',
            'table',
            'tbody',
            'td',
            'th',
            'thead',
            'tr',
            'br',
            ...customTags,
        ],
        ALLOWED_ATTR: ['href', 'class', 'target', 'rel', ...customAttrs],
        KEEP_CONTENT: true,
        ...options.purifyOptions,
    })
}

/**
 * Formats a risk value by removing a prefix and converting it to a human-readable format.
 * Expects the value to have a 4-character prefix (e.g., "risk_critical").
 *
 * @param {string} value - The risk value with prefix (e.g., "risk_high").
 * @returns {string|undefined} The formatted risk level (e.g., "High"), or "Info" if not found, or undefined if no value provided.
 */
const formatRisk = (value) => {
    const lookup = {
        critical: 'Critical',
        high: 'High',
        medium: 'Medium',
        low: 'Low',
        info: 'Info',
        investigate: 'Investigate',
    }
    if (value) {
        let risk = value.substring(4)
        if (lookup[risk]) {
            return lookup[risk]
        } else {
            return 'Info'
        }
    }
}

/**
 * Formats a status value into a human-readable format.
 * Uses a predefined lookup table, or falls back to title case conversion.
 *
 * @param {string} value - The status value (e.g., "unfixed", "confirmed").
 * @returns {string|undefined} The formatted status (e.g., "Unfixed"), or title-cased value if not in lookup, or undefined if no value provided.
 */
const formatStatus = (value) => {
    let lookup = {
        unfixed: 'Unfixed',
        confirmed: 'Confirmed',
        fixed: 'Fixed',
        false_positive: 'False Positive',
        acceptable_risk: 'Acceptable Risk',
    }
    if (value) {
        if (lookup[value]) {
            return lookup[value]
        } else {
            return toTitleCase(value)
        }
    }
}

export {
    toKebabCase,
    toTitleCase,
    toSentenceCase,
    toSentenceString,
    makeKey,
    isMarkdown,
    sanitizeHTML,
    formatRisk,
    formatStatus,
}
